/**
 * Regole sui documenti: formati ammessi e idoneita' alla pubblicazione.
 *
 * L'elenco dei formati e' quello dell'entita' `Documento` del modello dati.
 * JSON e' il formato di riferimento per i dati strutturati — massima
 * interoperabilita' e validazione automatizzabile — mentre PDF resta per i
 * documenti testuali, con la conformita' PDF/UA raccomandata.
 *
 * Va detto chiaramente: questo modulo serve a **guidare l'operatore**, non a
 * proteggere il sistema. Il controllo che conta — estensione effettiva, pagine
 * da scansione, PDF/UA — lo esegue la pipeline lato server, dove non e'
 * aggirabile.
 */

import type { Documento, FormatoDocumento, Pubblicazione } from "../api/types";

/** Estensioni ammesse dal modello dati (`Documento.formato`). */
export const ESTENSIONI_AMMESSE: readonly FormatoDocumento[] = [
  "json",
  "csv",
  "xml",
  "pdf",
  "xlsx",
];

/** Valore dell'attributo `accept` per il selettore di file. */
export const ACCETTA_ESTENSIONI = ESTENSIONI_AMMESSE.map((estensione) => `.${estensione}`).join(",");

/** Estensione in minuscolo di un nome file, senza il punto. */
export function estensioneDi(nomeFile: string): string {
  const punto = nomeFile.lastIndexOf(".");
  return punto === -1 ? "" : nomeFile.slice(punto + 1).toLowerCase();
}

/** Vero se l'estensione del file rientra fra quelle ammesse. */
export function estensioneAmmessa(nomeFile: string): boolean {
  return ESTENSIONI_AMMESSE.includes(estensioneDi(nomeFile) as FormatoDocumento);
}

// --------------------------------------------------------------------------- //
// Idoneita' alla pubblicazione
//
// Stavano nello stato del wizard di caricamento, che non esiste piu': la
// transizione bozza → pubblicato si comanda dall'alberatura, e la regola su
// quando quel comando e' lecito non puo' viaggiare con la pagina che lo mostra.
//
// Anche qui vale l'avvertenza di sopra: e' la regola che **guida l'operatore**,
// non quella che protegge il sistema. L'API rifiuta comunque la pubblicazione di
// una bozza con un controllo bloccante fallito; qui si evita di offrire un
// pulsante che finirebbe in un errore.
// --------------------------------------------------------------------------- //

/**
 * Vero se il documento ha superato la validazione al punto da poter essere
 * pubblicato.
 *
 * La regola e' **quella dell'API**, riga per riga
 * (`api/app/services/pubblicazione.py::_verifica_esiti_bloccanti`): impedisce la
 * pubblicazione **solo** un controllo bloccante fallito — il caso normativo e'
 * il PDF da scansione, che ai fini della trasparenza e' un documento non
 * pubblicato. Gli avvisi, per esempio la non conformita' PDF/UA, vengono
 * registrati e mostrati ma non fermano il flusso in questa fase del progetto.
 *
 * `in_corso` — e lo stato assente, che vale altrettanto — **non** blocca, ed e'
 * una correzione, non una dimenticanza. Due ragioni, entrambe verificate:
 *
 * 1. l'API lo accetta, e la sua docstring spiega perche': subordinare un
 *    obbligo di pubblicazione alla riuscita di un parser sarebbe un rischio
 *    maggiore di quello che si vuole prevenire;
 * 2. quello stato **non e' transitorio**. La validazione gira all'atto del
 *    caricamento e non viene mai ripetuta, quindi su un PDF cifrato o
 *    illeggibile `in_corso` e' definitivo: trattarlo come un blocco significava
 *    un documento impubblicabile per sempre, con un pulsante spento e nessuna
 *    via d'uscita.
 *
 * Resta comunque un'informazione da mostrare: vedi `validazioneNonConclusa`.
 */
export function documentoPubblicabile(documento: Documento): boolean {
  return !(documento.esiti_validazione?.controlli ?? []).some(
    (controllo) => controllo.esito === "fallito" && controllo.bloccante,
  );
}

/**
 * Vero se la pubblicazione ha qualcosa in lavorazione da rendere pubblico.
 *
 * Non basta `stato === "bozza"`, e questa e' la regola che mancava. Dal secondo
 * caricamento sullo stesso obbligo l'API **riapre la pubblicazione esistente**
 * (`bozza_per_caricamento_diretto` chiama `apri_versione_di_lavoro`) e la
 * restituisce con `stato` ancora `pubblicato`: nulla, lato server, riporta mai
 * lo stato a `bozza`. Guardare il solo stato lasciava ogni revisione
 * impubblicabile per sempre — cioe' il caso normale di un obbligo che si
 * aggiorna.
 *
 * Il segnale e' `versione_di_lavoro_aperta`, che l'API dichiara per questo e che
 * non si ricava dai `versioni`: quelli sono le sole versioni **pubblicate**.
 *
 * Sta qui e non nel componente perche' ci contano sopra anche i conteggi delle
 * due pagine — quanti contenuti aspettano su un ramo, quanti in tutto l'ente —
 * e un conteggio che divergesse dalla regola con cui si mostra il pulsante
 * mostrerebbe «3 in attesa» su un ramo senza un solo comando disponibile.
 */
export function inAttesaDiPubblicazione(pubblicazione: Pubblicazione): boolean {
  // Un contenuto archiviato non si pubblica, e la versione di lavoro puo'
  // restargli aperta: `archivia` cambia lo stato e non la chiude. Senza questa
  // riga il back-office offriva «Pubblica la revisione» su un archiviato — e
  // ogni clic riceveva un conflitto dall'API, che l'archiviazione la considera
  // definitiva. Il percorso non e' teorico: bastava caricare un secondo file su
  // un obbligo pubblicato e poi archiviarlo.
  if (pubblicazione.stato === "archiviato") return false;
  return (
    pubblicazione.stato === "bozza" || pubblicazione.versione_di_lavoro_aperta === true
  );
}

/**
 * Vero se ogni documento della bozza e' pubblicabile.
 *
 * La pubblicazione materializza **tutti** i documenti della versione in
 * lavorazione, non il solo ultimo caricato: l'idoneita' si verifica sull'intero
 * elenco. Una bozza senza documenti e' ammessa — ci sono obblighi che si
 * assolvono con il solo contenuto della pagina — e per questo `every` su un
 * elenco vuoto e' la risposta giusta, non un caso da intercettare.
 */
export function bozzaPubblicabile(pubblicazione: Pubblicazione): boolean {
  return (pubblicazione.documenti ?? []).every(documentoPubblicabile);
}

/**
 * Perche' una bozza non si puo' pubblicare, in una frase, o `null` se si puo'.
 *
 * Il pulsante disabilitato senza spiegazione e' il caso peggiore: l'operatore
 * vede un comando che non risponde e non ha modo di sapere che cosa gli manca.
 *
 * Il motivo e' uno solo perche' una sola cosa blocca, ed e' azionabile: il file
 * va sostituito. Quando i motivi erano due, il messaggio mostrato dipendeva
 * dall'ordine in cui li si interrogava, e su una bozza con un documento fallito
 * *e* uno non concluso diceva «attenda» invece di «sostituisca il file».
 */
export function motivoNonPubblicabile(pubblicazione: Pubblicazione): string | null {
  if (bozzaPubblicabile(pubblicazione)) return null;
  return "Un documento non ha superato un controllo bloccante. Va sostituito prima della pubblicazione: cio' che e' pubblicato non si cancella.";
}

/**
 * Vero se di almeno un documento non si conosce l'esito della validazione.
 *
 * Non impedisce di pubblicare — l'API non lo impedisce — ma si dichiara: chi
 * pubblica ha diritto di sapere che su quel file la pipeline non si e'
 * espressa, e che non si esprimera' piu' (l'analisi gira una volta, al
 * caricamento).
 */
export function validazioneNonConclusa(pubblicazione: Pubblicazione): boolean {
  return (pubblicazione.documenti ?? []).some((documento) => {
    const stato = documento.esiti_validazione?.stato;
    return stato === undefined || stato === "in_corso";
  });
}
